"use client"

import type React from "react"
import { createContext, useContext, useState, useCallback } from "react"
import { useToast } from "@/hooks/use-toast"
import { mockJobs, type MockJob } from "@/lib/mock-jobs-data"

export interface JobAssignment {
  candidateId: string
  jobId: string
  stage: string
  assignedDate: string
  assignedBy: string
}

export interface Job {
  id: string
  title: string
  department: string
  status: string
  hiringManagers: { name: string; role: string; email: string; avatar?: string }[]
  location: string
  type: string
  salary: string
  applicants: number
  interviews: number
  posted: string
  company: string
}

interface JobAssignmentsContextType {
  // State getters
  assignments: JobAssignment[]
  availableJobs: Job[]
  allJobs: Job[]

  // Getters
  getCandidatesForJob: (jobId: string, stage?: string) => JobAssignment[]
  getJobsForCandidate: (candidateId: string) => JobAssignment[]
  getCandidateIdsForJobStage: (jobId: string, stage: string) => string[]
  getJobById: (jobId: string) => Job | undefined

  // Setters/Actions
  assignCandidatesToJob: (candidateIds: string[], jobId: string, stage?: string) => boolean
  addCandidateToJob: (candidateId: string, jobId: string, stage?: string) => boolean
  moveCandidateStage: (candidateId: string, jobId: string, newStage: string) => void
  removeCandidateFromJob: (candidateId: string, jobId: string) => void
  updatePipelineData: (jobId: string, stageData: Record<string, string[]>) => void
  addJob: (jobData: Omit<Job, "id" | "applicants" | "interviews" | "posted">) => Job
}

const JobAssignmentsContext = createContext<JobAssignmentsContextType | undefined>(undefined)

function convertMockJobToJob(mockJob: MockJob): Job {
  return {
    id: mockJob.id,
    title: mockJob.title,
    department: mockJob.department,
    status: mockJob.status,
    hiringManagers: mockJob.hiringManagers,
    location: mockJob.location,
    type: mockJob.type,
    salary: mockJob.salary,
    applicants: mockJob.applicants,
    interviews: mockJob.interviews,
    posted: mockJob.posted,
    company: mockJob.company,
  }
}

export function JobAssignmentsProvider({ children }: { children: React.ReactNode }) {
  const [assignments, setAssignments] = useState<JobAssignment[]>([
    {
      candidateId: "1",
      jobId: "1",
      stage: "longlist",
      assignedDate: new Date().toISOString(),
      assignedBy: "Current User",
    },
    {
      candidateId: "2",
      jobId: "1",
      stage: "shortlist",
      assignedDate: new Date().toISOString(),
      assignedBy: "Current User",
    },
    {
      candidateId: "3",
      jobId: "2",
      stage: "longlist",
      assignedDate: new Date().toISOString(),
      assignedBy: "Current User",
    },
    {
      candidateId: "644525821",
      jobId: "1",
      stage: "longlist",
      assignedDate: new Date().toISOString(),
      assignedBy: "Current User",
    },
    {
      candidateId: "644525820",
      jobId: "1",
      stage: "interview",
      assignedDate: new Date().toISOString(),
      assignedBy: "Current User",
    },
    {
      candidateId: "644525819",
      jobId: "1",
      stage: "shortlist",
      assignedDate: new Date().toISOString(),
      assignedBy: "Current User",
    },
  ])

  const [jobs, setJobs] = useState<Job[]>(mockJobs.map(convertMockJobToJob))
  const allJobs = jobs
  const availableJobs = allJobs.filter((job) => job.status === "active")
  const { toast } = useToast()

  const getCandidatesForJob = useCallback(
    (jobId: string, stage?: string) => {
      return assignments.filter((a) => a.jobId === jobId && (!stage || a.stage === stage))
    },
    [assignments],
  )

  const getJobsForCandidate = useCallback(
    (candidateId: string) => {
      return assignments.filter((a) => a.candidateId === candidateId)
    },
    [assignments],
  )

  const getCandidateIdsForJobStage = useCallback(
    (jobId: string, stage: string) => {
      const result = assignments.filter((a) => a.jobId === jobId && a.stage === stage).map((a) => a.candidateId)
      return result
    },
    [assignments],
  )

  const getJobById = useCallback(
    (jobId: string) => {
      return allJobs.find((job) => job.id === jobId)
    },
    [allJobs],
  )

  const assignCandidatesToJob = useCallback(
    (candidateIds: string[], jobId: string, stage = "longlist") => {
      const job = availableJobs.find((j) => j.id === jobId)

      if (!job) {
        toast({
          title: "Error",
          description: "Job not found",
          variant: "destructive",
        })
        return false
      }

      const newAssignments: JobAssignment[] = candidateIds.map((candidateId) => ({
        candidateId,
        jobId,
        stage,
        assignedDate: new Date().toISOString(),
        assignedBy: "Current User",
      }))

      setAssignments((prev) => {
        // Remove existing assignments for these candidates to this job
        const filtered = prev.filter((a) => !(candidateIds.includes(a.candidateId) && a.jobId === jobId))
        const updated = [...filtered, ...newAssignments]

        return updated
      })

      toast({
        title: "Candidates assigned successfully",
        description: `${candidateIds.length} candidate(s) added to ${job.title} in ${stage} stage`,
      })

      return true
    },
    [availableJobs, toast],
  )

  const addCandidateToJob = useCallback(
    (candidateId: string, jobId: string, stage = "longlist") => {
      return assignCandidatesToJob([candidateId], jobId, stage)
    },
    [assignCandidatesToJob],
  )

  const moveCandidateStage = useCallback(
    (candidateId: string, jobId: string, newStage: string) => {
      setAssignments((prev) =>
        prev.map((a) => (a.candidateId === candidateId && a.jobId === jobId ? { ...a, stage: newStage } : a)),
      )

      const job = availableJobs.find((j) => j.id === jobId)
      toast({
        title: "Stage updated",
        description: `Candidate moved to ${newStage} in ${job?.title}`,
      })
    },
    [availableJobs, toast],
  )

  const removeCandidateFromJob = useCallback(
    (candidateId: string, jobId: string) => {
      setAssignments((prev) => prev.filter((a) => !(a.candidateId === candidateId && a.jobId === jobId)))

      const job = availableJobs.find((j) => j.id === jobId)
      toast({
        title: "Candidate removed",
        description: `Candidate removed from ${job?.title}`,
      })
    },
    [availableJobs, toast],
  )

  const updatePipelineData = useCallback((jobId: string, stageData: Record<string, string[]>) => {
    setAssignments((prev) => {
      // Remove all existing assignments for this job
      const filtered = prev.filter((a) => a.jobId !== jobId)

      // Create new assignments based on stage data
      const newAssignments: JobAssignment[] = []
      Object.entries(stageData).forEach(([stage, candidateIds]) => {
        candidateIds.forEach((candidateId) => {
          newAssignments.push({
            candidateId,
            jobId,
            stage,
            assignedDate: new Date().toISOString(),
            assignedBy: "Current User",
          })
        })
      })

      return [...filtered, ...newAssignments]
    })
  }, [])

  const addJob = useCallback(
    (jobData: Omit<Job, "id" | "applicants" | "interviews" | "posted">) => {
      const newJob: Job = {
        ...jobData,
        id: Date.now().toString(),
        applicants: 0,
        interviews: 0,
        posted: new Date().toLocaleDateString(),
      }

      setJobs((prev) => [...prev, newJob])

      toast({
        title: "Job created successfully",
        description: `${newJob.title} has been added to your job listings`,
      })

      return newJob
    },
    [toast],
  )

  const contextValue: JobAssignmentsContextType = {
    assignments,
    availableJobs,
    allJobs,
    getCandidatesForJob,
    getJobsForCandidate,
    getCandidateIdsForJobStage,
    getJobById,
    assignCandidatesToJob,
    addCandidateToJob,
    moveCandidateStage,
    removeCandidateFromJob,
    updatePipelineData,
    addJob,
  }

  return <JobAssignmentsContext.Provider value={contextValue}>{children}</JobAssignmentsContext.Provider>
}

export function useJobAssignments() {
  const context = useContext(JobAssignmentsContext)
  if (context === undefined) {
    throw new Error("useJobAssignments must be used within a JobAssignmentsProvider")
  }
  return context
}
